import type { MissionAssessmentListItemDto } from '@@types/api.types';
import { TECHNOLOGY_TAB_HISTORY_ITEM_CLASS } from '@components/pages/technology/components/shared/technology-container-query.constants';
import { cn } from '@lib/utils';
import { formatDate } from '@utils/date.util';
import { FC } from 'react';
import { MISSION_ASSESSMENT_ACCESS_RESTRICTED_MESSAGE } from '../../../../mission-assessment/mission-assessment.constants';
import {
  MaSidebarAssessmentList,
  type MaSidebarAssessmentListRenderItemParams,
} from './ma-sidebar-assessment-list.component';

type MaExpertAssessmentListProps = {
  isOverviewActive: boolean;
  isLoading?: boolean;
  onSelectAssessment: (assessmentId: string) => void;
  onSelectOverview: () => void;
  selectedAssessmentId: string | null;
  versions: MissionAssessmentListItemDto[];
};

export const MaExpertAssessmentList: FC<MaExpertAssessmentListProps> = (props) => {
  const { isLoading, isOverviewActive, onSelectAssessment, onSelectOverview, selectedAssessmentId, versions } = props;

  return (
    <MaSidebarAssessmentList
      emptyText="No assessments yet."
      isLoading={isLoading}
      isOverviewActive={isOverviewActive}
      onSelectAssessment={onSelectAssessment}
      onSelectOverview={onSelectOverview}
      renderItem={(renderItemProps) => <MaExpertAssessmentItem {...renderItemProps} />}
      selectedAssessmentId={selectedAssessmentId}
      title="My Assessments"
      titleClassName="my-1 text-sm font-medium text-gray-600"
      versions={versions}
    />
  );
};

type MaExpertAssessmentItemProps = MaSidebarAssessmentListRenderItemParams;

const MaExpertAssessmentItem: FC<MaExpertAssessmentItemProps> = (props) => {
  const { isSelected, onPrefetch, onSelect, version } = props;

  const isAccessRestricted = Boolean(version.accessRestricted);
  const restrictedMessage = version.accessRestrictedMessage ?? MISSION_ASSESSMENT_ACCESS_RESTRICTED_MESSAGE;
  const useName = version.request?.use?.name;
  const domainName = version.request?.domain?.name;
  const hasUseDomain = Boolean(useName || domainName);

  return (
    <button
      className={cn(
        'flex flex-col gap-1 rounded-md p-3 text-left transition-all duration-200',
        TECHNOLOGY_TAB_HISTORY_ITEM_CLASS,
        isAccessRestricted
          ? 'cursor-not-allowed border border-amber-200 bg-amber-50 text-amber-800'
          : isSelected
            ? 'cursor-pointer ring-2 ring-[#3D2EFFCC] ring-inset bg-linear-to-b bg-[#3D2EFF26] text-[#3D2EFF]'
            : 'cursor-pointer bg-gray-100 text-gray-600 hover:ring-2 hover:ring-[#3D2EFFCC]',
      )}
      onClick={() => {
        if (isAccessRestricted) {
          return;
        }
        onSelect();
      }}
      onMouseEnter={() => {
        if (isAccessRestricted) {
          return;
        }
        onPrefetch();
      }}
      type="button"
    >
      <span className="text-xs font-medium">{formatDate(version.updatedAt)}</span>

      {hasUseDomain && (
        <div className="flex items-center gap-1 text-xs text-gray-500">
          {useName && <span>{useName}</span>}
          {useName && domainName && <span>•</span>}
          {domainName && <span>{domainName}</span>}
        </div>
      )}

      {isAccessRestricted && <span className="text-[11px] text-amber-800">{restrictedMessage}</span>}
    </button>
  );
};
